import { mdiArrowLeftRight } from '@mdi/js';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp, TableCell } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { Wallet } from 'common/state/banking/actions';
import {
  ClientTransfer,
  ClientTransferStatuses,
} from 'common/state/banking/transfers/actions';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TransferCurrencyMap } from '../customer';

export interface TransfersTable {
  transfers: TransferCurrencyMap;
}

export const TotalCell = styled(TableCell)`
  border-top: 1px solid ${({ theme }) => theme.foreground};
  background: transparent !important;
`;
export const TotalLabelCell = styled(TotalCell)`
  grid-column: 1/-2;
  text-align: right;
`;

export const TotalValueCell = styled(TotalCell)``;

export const TransfersTable = ({ transfers }: TransfersTable) => {
  const [destinations, setDestinations] = useState<{ [url: number]: Wallet }>(
    {}
  );

  const columns = useMemo(
    (): ColumnProp<ClientTransfer>[] => [
      { key: 'date', title: 'Date' },
      { key: 'description', title: 'Description' },
      {
        key: 'destination',
        title: 'Destination',
        valueFormatter: (row) =>
          destinations?.[row.destination]?.title || 'Loading...',
      },
      {
        key: 'contact',
        title: 'Contact',
        valueFormatter: (row) =>
          destinations?.[row.destination]?.owner.split('/').slice(-2)[0] ||
          'Loading...',
      },
      { key: 'reference', title: 'Reference' },
      {
        key: 'currency_bookie',
        title: 'Currency (Bookie)',
        valueFormatter: (_row) => '-',
      },
      {
        key: 'amount_bookie',
        title: 'Amount (Bookie)',
        valueFormatter: (_row) => '-',
      },
      { key: 'status', title: 'Status' },
      { key: 'currency', title: 'Currency (Customer)' },
      {
        key: 'amount',
        title: 'Amount (Customer)',
        valueFormatter: (row) => (
          <div
            style={
              row.status === ClientTransferStatuses.UNACCEPTED
                ? {
                    opacity: 0.5,
                    textDecoration: 'line-through',
                  }
                : undefined
            }
          >
            {new Currency(row.amount, row.currency).toElement()}
          </div>
        ),
        csvFormatter: (row) => {
          const s = new Currency(row.amount, row.currency).toString({
            symbol: false,
          });
          return row.status === ClientTransferStatuses.UNACCEPTED
            ? s
                .split('')
                .map((char) => char + '\u0336')
                .join('')
            : s;
        },
      },
    ],
    [destinations]
  );

  const totalColumns = useMemo((): Set<string> => new Set(['amount']), []);

  useEffect(() => {
    const newUrls = new Set<string>();
    Array.from(Object.values(transfers))
      .flat()
      .forEach((transfer) => {
        if (!(transfer.destination in destinations))
          newUrls.add(transfer.destination);
      });

    Promise.all(
      Array.from(newUrls).map((url) =>
        api.get<Wallet>(url).then((response) => response.data)
      )
    ).then((wallets) => {
      setDestinations(
        wallets.reduce((acc, wallet) => ({ ...acc, [wallet.url]: wallet }), {
          ...destinations,
        })
      );
    });
    // eslint-disable-next-line
  }, [transfers]);

  return (
    <>
      {Object.entries(transfers).map(([currency, transfers]) => (
        <React.Fragment key={currency}>
          <TableTitle
            icon={mdiArrowLeftRight}
            title={`${currency} Transfers`}
          />
          <BaseTable
            columns={columns}
            rows={transfers}
            csvName="Transfers"
            totalColumns={totalColumns}
            totalRowFilter={(row, key) =>
              key === 'amount' &&
              row.status === ClientTransferStatuses.UNACCEPTED
                ? false
                : true
            }
            paginate={false}
          />
        </React.Fragment>
      ))}
    </>
  );
};
